import { Injectable, signal, WritableSignal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, finalize } from 'rxjs/operators';
import { Factura } from '@app/types';
import { ApiResponse } from '@app/types';

@Injectable({
    providedIn: 'root',
})
export class InvoiceDataService {
    private readonly http = inject(HttpClient);

    private _facturas: WritableSignal<Factura[]> = signal([]);
    public readonly facturasSignal = this._facturas.asReadonly();

    private _isLoading: WritableSignal<boolean> = signal(true);
    public readonly isLoadingSignal = this._isLoading.asReadonly();

    private readonly API_URL = environment.API_URL;

    constructor() {
        // Carga inicial eliminada de aquí
    }

    // Always fetch from API; cache fully bypassed and no write-through
    loadInvoices(forceRefresh: boolean = true): Observable<Factura[]> {
        this._isLoading.set(true);

        const body = { filters: {}, limit: 2000, offset: 0, include_relations: true, include_metadata: true };
        return this.http.post<ApiResponse<Factura>>(`${this.API_URL}/invoices/read`, body).pipe(
            map((response) => {
                const data = this.processInvoiceResponse(response.data);
                this._facturas.set(data);
                return data;
            }),
            catchError((err) => {
                console.error('Error fetching invoices from API', err);
                return throwError(() => err);
            }),
            finalize(() => this._isLoading.set(false))
        );
    }

    loadPreInvoices(): Observable<Factura[]> {
        this._isLoading.set(true);
        const body = { filters: {}, limit: 2000, offset: 0 };
        return this.http.post<ApiResponse<Factura>>(`${this.API_URL}/pre-invoices/read`, body).pipe(
            map((response) => {
                const data = this.processInvoiceResponse(response.data);
                this._facturas.set(data);
                return data;
            }),
            catchError((err) => {
                console.error('Error fetching pre-invoices', err);
                this._facturas.set([]);
                return throwError(() => err);
            }),
            finalize(() => this._isLoading.set(false))
        );
    }

    loadReferrals(): Observable<Factura[]> {
        this._isLoading.set(true);
        const body = { filters: {}, limit: 2000, offset: 0 };
        return this.http.post<ApiResponse<Factura>>(`${this.API_URL}/referrals/read`, body).pipe(
            map((response) => {
                const data = this.processInvoiceResponse(response.data);
                this._facturas.set(data);
                return data;
            }),
            catchError((err) => {
                console.error('Error fetching referrals', err);
                this._facturas.set([]);
                return throwError(() => err);
            }),
            finalize(() => this._isLoading.set(false))
        );
    }

    private processInvoiceResponse(data: any[]): Factura[] {
        return data.map((f) => ({
            ...f,
            fechaEmision: new Date(f.fechaEmision),
            fechaTimbrado: new Date(f.fechaTimbrado),
            fechaVencimiento: f.fechaVencimiento ? new Date(f.fechaVencimiento) : undefined,
            statusHistory: (f.statusHistory || []).map((h: any) => ({ ...h, fecha: new Date(h.fecha) })),
            seleccionada: false,
        }));
    }

    // Force reload after mutation and emit fresh list to subscribers
    aplicarCambioEstatus(facturasIds: string[], nuevoEstatus: string, observacion: string): Observable<ApiResponse<Factura>> {
        const newHistoryEntry = {
            estatus: nuevoEstatus,
            fecha: new Date().toISOString(),
            observacion: observacion,
            userId: localStorage.getItem('userId') || 'unknown'
        };

        const updates = facturasIds.map((id) => ({
            id: id,
            changes: {
                estatus: nuevoEstatus,
                statusHistory: newHistoryEntry,
            },
            operation_description: `Cambio de estatus a ${nuevoEstatus}`
        }));

        return this.http.patch<ApiResponse<Factura>>(`${this.API_URL}/invoices`, updates).pipe(
            switchMap((response) => {
                // Validar por la presencia de 'data' en lugar de 'state'
                if (response && (response as any).data) {
                    // HARD REFRESH: force a complete reload and emit a fresh array reference
                    return this.loadInvoices(true).pipe(map(() => response));
                }
                // Si no es exitosa, simplemente pasamos la respuesta como viene
                return of(response);
            }),
            catchError((err) => {
                console.error('[InvoiceDataService] Error en la llamada PATCH:', err);
                return throwError(() => err);
            }),
        );
    }
}
